import React, { useEffect, useMemo, useState } from "react";
import Layout from "../layout/Layout";
import LoadingOverlay from "react-loading-overlay-ts";
import Api from "../Config/ApiConfig";
import Pagination from "../Component/Pagination";
import { toast } from "react-toastify";

export default function OtpExceptions() {
  const [loading, setLoading] = useState(true);
  const [exceptions, setExceptions] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [identifier, setIdentifier] = useState("");
  const [type, setType] = useState("NO_SEND_ALWAYS_APPROVE");
  const [fetchError, setFetchError] = useState("");

  const fetchData = async () => {
    try {
      setLoading(true);
      setFetchError("");
      const response = await Api.get("/otp-exceptions");
      if (response.data && response.data.success) {
        setExceptions(response.data.data || []);
      }
    } catch (err) {
      const msg =
        err?.response?.status === 401
          ? "Unauthorized: Please log in again to view OTP exceptions."
          : "Failed to load OTP exceptions. Check network and API base URL.";
      setFetchError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const validateIdentifier = (value) => {
    const trimmed = value.trim();
    if (!trimmed) {
      toast.error("Please enter an email or phone number");
      return null;
    }
    const isEmail = trimmed.includes("@");
    const isPhone = trimmed.startsWith("+") && trimmed.replace(/[^\d]/g, "").length >= 8;
    if (!isEmail && !isPhone) {
      toast.error("Use a valid email or phone with country code (e.g. +911234567890)");
      return null;
    }
    return trimmed;
  };

  const handleCreate = async () => {
    const trimmed = validateIdentifier(identifier);
    if (!trimmed) {
      return;
    }
    const existing = exceptions.find(
      (e) => String(e.identifier).toLowerCase() === trimmed.toLowerCase()
    );
    if (existing && existing.type === type) {
      toast.info("This identifier already has the same OTP exception type");
      return;
    }
    if (existing && existing.type !== type) {
      toast.info("Updating existing OTP exception type for this identifier");
    }
    try {
      setLoading(true);
      const response = await Api.post("/otp-exceptions", {
        identifier: trimmed,
        type,
      });
      if (response.data && response.data.success) {
        await fetchData();
        setIdentifier("");
        toast.success("OTP exception saved");
      }
    } catch (error) {
      toast.error("Failed to save OTP exception");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!id) {
      return;
    }
    try {
      setLoading(true);
      const response = await Api.delete(`/otp-exceptions/${id}`);
      if (response.data && response.data.success) {
        await fetchData();
        toast.success("OTP exception removed");
      }
    } catch (error) {
      toast.error("Failed to remove OTP exception");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateType = async (item, newType) => {
    if (!newType || newType === item.type) {
      return;
    }
    try {
      setLoading(true);
      const response = await Api.put(`/otp-exceptions/${item.id}`, {
        type: newType,
      });
      if (response.data && response.data.success) {
        await fetchData();
        toast.success("OTP exception type updated");
      }
    } catch (error) {
      toast.error("Failed to update OTP exception type");
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return exceptions;
    return exceptions.filter(
      (e) =>
        String(e.identifier).toLowerCase().includes(s) ||
        String(e.type).toLowerCase().includes(s) ||
        String(e.status).toLowerCase().includes(s)
    );
  }, [exceptions, search]);

  const pageCount = Math.ceil(filtered.length / limit) || 1;
  const start = (page - 1) * limit;
  const current = filtered.slice(start, start + limit);

  const handlePageClick = (e) => {
    setPage(e.selected + 1);
  };

  const renderTypeLabel = (value) => {
    if (value === "NO_SEND_ALWAYS_APPROVE") {
      return "Always Approve (No Send)";
    }
    if (value === "SEND_ALWAYS_APPROVE") {
      return "Always Approve (Send)";
    }
    return value;
  };

  const renderTypeHelp = () => {
    return (
      <div
        style={{
          marginBottom: 16,
          padding: 12,
          borderRadius: 8,
          background: "#f1f5f9",
          color: "#0f172a",
          fontSize: 13,
          lineHeight: 1.5,
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: 4 }}>How OTP exceptions work</div>
        <div>
          <strong>Always Approve (No Send):</strong> OTP is not sent and any OTP entered is
          treated as valid. Ideal for internal testing or demo accounts.
        </div>
        <div style={{ marginTop: 4 }}>
          <strong>Always Approve (Send):</strong> OTP is sent normally, but verification is
          always approved. Useful when you want a real user experience but no failures.
        </div>
        <div style={{ marginTop: 4 }}>
          Identifiers can be an email address or full phone number with country code, for
          example <span style={{ fontFamily: "monospace" }}>+911234567890</span>.
        </div>
      </div>
    );
  };

  return (
    <Layout>
      <LoadingOverlay active={loading} text="Loading data...">
        <div className="dashboard_data_wrap">
          <div className="dashbord_cont_wrapper">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                marginBottom: 16,
                gap: 8,
              }}
            >
              <div>
                <h2
                  style={{
                    fontSize: 22,
                    fontWeight: 700,
                    margin: 0,
                    color: "#0f172a",
                  }}
                >
                  OTP Exceptions
                </h2>
                <p
                  style={{
                    margin: "6px 0 0",
                    fontSize: 14,
                    color: "#374151",
                  }}
                >
                  Control which identifiers can bypass OTP checks for faster testing
                  and internal accounts.
                </p>
              </div>
            </div>
            {renderTypeHelp()}
            {fetchError && (
              <div
                style={{
                  background: "#fee2e2",
                  color: "#7f1d1d",
                  border: "1px solid #fecaca",
                  padding: 12,
                  borderRadius: 8,
                  marginBottom: 12,
                  fontSize: 14,
                }}
              >
                <i className="fas fa-exclamation-triangle" style={{ marginRight: 8 }} />
                {fetchError}
              </div>
            )}
            <div
              style={{
                marginBottom: 20,
                padding: 16,
                borderRadius: 12,
                background: "#f8fafc",
                color: "#0f172a",
                border: "1px solid #e5e7eb",
                display: "flex",
                flexWrap: "wrap",
                gap: 12,
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div style={{ flex: "1 1 260px" }}>
                <div
                  style={{
                    fontSize: 14,
                    marginBottom: 6,
                    fontWeight: 600,
                    color: "#0f172a",
                  }}
                >
                  Add a new OTP exception or update an existing one
                </div>
                <input
                  type="text"
                  placeholder="Email or phone (e.g. +911234567890)"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: 8,
                    border: "1px solid #cbd5e1",
                    background: "#ffffff",
                    color: "#111827",
                    fontSize: 14,
                  }}
                />
              </div>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <select
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                  style={{
                    padding: "10px 12px",
                    borderRadius: 8,
                    border: "1px solid #cbd5e1",
                    background: "#ffffff",
                    color: "#111827",
                    fontSize: 14,
                    minWidth: 200,
                  }}
                >
                  <option value="NO_SEND_ALWAYS_APPROVE">
                    Always Approve (No Send)
                  </option>
                  <option value="SEND_ALWAYS_APPROVE">
                    Always Approve (Send)
                  </option>
                </select>
                <button
                  className="common-btn"
                  onClick={handleCreate}
                  disabled={loading}
                  style={{
                    padding: "10px 16px",
                    fontSize: 14,
                    borderRadius: 999,
                    opacity: loading ? 0.7 : 1,
                  }}
                >
                  {loading ? "Saving..." : "Save exception"}
                </button>
              </div>
            </div>
            <div className="search-container">
              <div className="search-box">
                <span>
                  <i className="fas fa-search"></i>
                </span>
                <input
                  type="text"
                  placeholder="Search identifier, type or status..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  style={{
                    color: "#111827",
                    background: "#ffffff",
                    border: "1px solid #e5e7eb",
                    padding: "8px 10px",
                    borderRadius: 8,
                    fontSize: 14,
                  }}
                />
                <button
                  className="search-button"
                  onClick={() => {
                    setSearch("");
                    setPage(1);
                  }}
                  style={{
                    padding: "8px 12px",
                    borderRadius: 8,
                    fontSize: 14,
                  }}
                >
                  Clear
                </button>
              </div>
              <div className="export-options">
                <select
                  className="common-btn"
                  value={limit}
                  onChange={(e) => {
                    setLimit(Number(e.target.value));
                    setPage(1);
                  }}
                  style={{
                    padding: "8px 10px",
                    borderRadius: 8,
                    fontSize: 14,
                  }}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
              </div>
            </div>

            <div className="user_list_wrap table-responsive">
              <table
                className="table table-stripd"
                style={{ color: "#111827", fontSize: 14 }}
              >
                <thead>
                  <tr>
                    <th>Identifier</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {current.map((e, i) => (
                    <tr key={i}>
                      <td>{e.identifier}</td>
                      <td>{renderTypeLabel(e.type)}</td>
                      <td>
                        {e.status === "Always Approve (No Send)" ? (
                          <div
                            className="active_button-block"
                            style={{
                              background: "#fde68a",
                              color: "#78350f",
                              borderRadius: 8,
                              padding: "6px 10px",
                              display: "inline-block",
                            }}
                          >
                            {e.status}
                          </div>
                        ) : (
                          <div
                            className="active_button"
                            style={{
                              background: "#d1fae5",
                              color: "#065f46",
                              borderRadius: 8,
                              padding: "6px 10px",
                              display: "inline-block",
                            }}
                          >
                            {e.status}
                          </div>
                        )}
                      </td>
                      <td>
                        <div
                          style={{
                            display: "flex",
                            gap: 8,
                            alignItems: "center",
                            flexWrap: "wrap",
                          }}
                        >
                          <select
                            value={e.type}
                            onChange={(event) =>
                              handleUpdateType(e, event.target.value)
                            }
                            style={{
                              padding: "6px 8px",
                              borderRadius: 8,
                              border: "1px solid #e5e7eb",
                              background: "#ffffff",
                              color: "#111827",
                              fontSize: 13,
                            }}
                          >
                            <option value="NO_SEND_ALWAYS_APPROVE">
                              Always Approve (No Send)
                            </option>
                            <option value="SEND_ALWAYS_APPROVE">
                              Always Approve (Send)
                            </option>
                          </select>
                          <button
                            className="common-btn"
                            style={{
                              padding: "6px 10px",
                              fontSize: 13,
                              borderRadius: 8,
                            }}
                            onClick={() => handleDelete(e.id)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!loading && current.length === 0 && (
                    <tr>
                      <td colSpan={4} style={{ textAlign: "center" }}>
                        No exceptions found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <Pagination handlePageClick={handlePageClick} pageCount={pageCount} />
          </div>
        </div>
      </LoadingOverlay>
    </Layout>
  );
}
